import {
  LockOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  UnlockOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Alert,
  App,
  Avatar,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/entities/session";
import type { AdminAccount } from "@/entities/admin";
import { useAdmins } from "@/entities/admin";
import type { AdminCreateBody } from "@/features/manage-admins";
import { useCreateAdmin, useUpdateAdmin } from "@/features/manage-admins";
import { errorMessage } from "@/shared/api";
import { SECTIONS, sectionSurface } from "@/shared/config";
import { formatDateTime, onModalOk } from "@/shared/lib";
import { EmptyState, PageHeader, SectionCard } from "@/shared/ui";

/** Срок в прошлом блокировкой уже не является: бэкенд такую учётку пускает. */
function isLocked(admin: AdminAccount): boolean {
  return admin.locked_until != null && new Date(admin.locked_until) > new Date();
}

export function AdminsPage() {
  const { t } = useTranslation();
  const { modal, message } = App.useApp();
  const { admin: current } = useAuth();
  const { data, isPending, isError, error } = useAdmins();
  const create = useCreateAdmin();
  const update = useUpdateAdmin();

  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<AdminCreateBody>();

  const columns: ColumnsType<AdminAccount> = [
    {
      title: t("admins.login"),
      key: "login",
      render: (_, row) => {
        // Цвет аватара по роли — та же пара, что у тега роли в шапке: беглым
        // взглядом по списку видно, сколько в проекте суперадминов.
        const color =
          row.role === "superadmin" ? SECTIONS.admins.color : SECTIONS.audit.color;
        return (
          <Space size={10} align="center">
            <Avatar
              size={32}
              style={{ background: sectionSurface(color), color, fontWeight: 600, flex: "none" }}
            >
              {row.login.charAt(0).toUpperCase()}
            </Avatar>
            <Typography.Text strong>{row.login}</Typography.Text>
          </Space>
        );
      },
    },
    {
      title: t("admins.role"),
      key: "role",
      width: 250,
      render: (_, row) => (
        <Select
          style={{ width: 220 }}
          value={row.role}
          // Собственную роль сменить нельзя: суперадмин, понизивший себя,
          // запер бы админку — вернуть права было бы некому.
          disabled={row.id === current?.id}
          onChange={(role) => {
            update.mutate(
              { id: row.id, body: { role } },
              {
                onSuccess: () => {
                  void message.success(t("admins.updated"));
                },
              },
            );
          }}
          options={[
            { value: "superadmin", label: t("app.role.superadmin") },
            { value: "operator", label: t("app.role.operator") },
          ]}
        />
      ),
    },
    {
      title: t("admins.isActive"),
      key: "is_active",
      width: 130,
      render: (_, row) => (
        <Switch
          checked={row.is_active}
          disabled={row.id === current?.id}
          onChange={(checked) => {
            update.mutate(
              { id: row.id, body: { is_active: checked } },
              {
                onSuccess: () => {
                  void message.success(t("admins.updated"));
                },
              },
            );
          }}
        />
      ),
    },
    {
      title: t("admins.lastLogin"),
      key: "last_login_at",
      render: (_, row) =>
        row.last_login_at ? (
          formatDateTime(row.last_login_at)
        ) : (
          <Typography.Text type="secondary">{t("admins.never")}</Typography.Text>
        ),
    },
    {
      title: t("admins.lockedUntil"),
      key: "locked_until",
      render: (_, row) =>
        // Форма входа о блокировке молчит намеренно (один текст на все отказы),
        // поэтому эта колонка — единственное место, где причину вообще видно.
        isLocked(row) ? (
          <Typography.Text type="warning">{formatDateTime(row.locked_until ?? "")}</Typography.Text>
        ) : (
          <Typography.Text type="secondary">{t("admins.notLocked")}</Typography.Text>
        ),
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 220,
      render: (_, row) => (
        <Button
          icon={<UnlockOutlined aria-hidden />}
          disabled={!isLocked(row)}
          onClick={() => {
            void modal.confirm({
              title: t("admins.unlock"),
              content: t("admins.unlockConfirm"),
              okText: t("admins.unlock"),
              cancelText: t("common.cancel"),
              onOk: async () => {
                await update.mutateAsync({ id: row.id, body: { unlock: true } });
                void message.success(t("admins.unlocked"));
              },
            });
          }}
        >
          {t("admins.unlock")}
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        section="admins"
        title={t("admins.title")}
        subtitle={t("admins.subtitle")}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined aria-hidden />}
            onClick={() => {
              setCreating(true);
            }}
          >
            {t("admins.create")}
          </Button>
        }
      />

      <Alert
        type="info"
        showIcon
        title={t("admins.selfWarning")}
        style={{ marginBottom: 16 }}
      />

      {isError && (
        <Alert type="error" showIcon title={errorMessage(error)} style={{ marginBottom: 16 }} />
      )}

      <SectionCard color={SECTIONS.admins.color} bodyPadding={0}>
        <Table<AdminAccount>
          rowKey="id"
          columns={columns}
          dataSource={data ?? []}
          loading={isPending}
          pagination={false}
          scroll={{ x: 1100 }}
          locale={{
            emptyText: (
              <EmptyState icon={<SafetyCertificateOutlined aria-hidden />} title={t("admins.empty")} />
            ),
          }}
        />
      </SectionCard>

      <Modal
        open={creating}
        title={t("admins.create")}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        okButtonProps={{ loading: create.isPending }}
        onCancel={() => {
          setCreating(false);
        }}
        onOk={onModalOk(form, async (values) => {
          await create.mutateAsync(values);
          form.resetFields();
          setCreating(false);
          void message.success(t("admins.created"));
        })}
      >
        <Form<AdminCreateBody> form={form} layout="vertical" initialValues={{ role: "operator" }}>
          <Form.Item
            name="login"
            label={t("admins.login")}
            rules={[
              { required: true, message: t("login.required") },
              { pattern: /^[a-zA-Z0-9._-]{3,64}$/, message: "Латиница, цифры, точка, дефис" },
            ]}
          >
            <Input autoFocus autoComplete="off" prefix={<UserOutlined aria-hidden />} />
          </Form.Item>
          <Form.Item
            name="password"
            label={t("admins.password")}
            extra={t("admins.passwordHint")}
            rules={[{ required: true, min: 12, message: t("admins.passwordHint") }]}
          >
            <Input.Password autoComplete="new-password" prefix={<LockOutlined aria-hidden />} />
          </Form.Item>
          <Form.Item name="role" label={t("admins.role")}>
            <Select
              options={[
                { value: "operator", label: t("app.role.operator") },
                { value: "superadmin", label: t("app.role.superadmin") },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
